import CircularProgress from '@material-ui/core/CircularProgress';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import GetAppIcon from '@material-ui/icons/GetApp';
import ReplayIcon from '@material-ui/icons/Replay';
import {
  useAlert,
  useMajorTest,
  useMeaningValueTestResult,
  useMyResulatOfTest,
  useNeedsProTestResult,
  useNeedsTestResult,
  usePreferencePersonalityTestResult,
  useStudyTestResult,
} from '@orientaction/hooks';
import { ResultServices } from '@orientaction/services';
import { getBC, getFromLS, getPeriodeRealisation, getRestartedResultId } from '@orientaction/utils';
import clsx from 'clsx';
import dateFormat from 'dateformat';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getMe } from '../../../../services/user.service';
import { apiServiceNative } from '../../../../utils/fetchNative';
import NeedsProResultPDF from '../../../PageResultat/NeedsProResult/ResultatPDF';
import NeedsResultPDF from '../../../PageResultat/NeedsResult/ResultatPDF';
import HarmonyResultPDF from '../../../PageResultat/ResultatHarmony/ResultatPDF';
import MeaningResultPDF from '../../../PageResultat/ResultatMeanning/ResultatPDF';
import PreferenceResultPDF from '../../../PageResultat/ResultatPreference/ResultatPDF';
import PreferenceJeuneResultPDF from '../../../PageResultat/ResultatPreference/ResultatPDFJeune';
import StudyResultPDF from '../../../PageResultat/ResultatStudy/ResultatPDF';
import {
  EmptyState,
  PageHeader,
  RedesignLayout,
  SearchInput,
  SectionHeader,
  useSharedStyles,
} from '../../shared';
import { redesignTheme } from '../../theme';
import { getMajorTestConfig } from '../Major/Result/config/majorTestConfigs';
import { useMyResultsStyles } from './index.styles';
import { CompletedGameResult, fetchCompletedGameResults } from './utils';
import { openMyResultRetake } from './openMyResultRetake';

type ResultCategory = 'major' | 'minor' | 'game';
type MajorResultKind =
  | 'meaning'
  | 'needs'
  | 'needsPro'
  | 'preference'
  | 'harmony'
  | 'study'
  | 'preferenceJeune';

type ResultItem = {
  id: string | number;
  title: string;
  date: string;
  time: string;
  createdAt: string;
  searchText: string;
  consultPath: string;
  retakePath?: string;
  category: ResultCategory;
  majorKind?: MajorResultKind;
  gameId?: number;
};

type MajorResultConfig = {
  key: string;
  kind: MajorResultKind;
  title: string;
  path: (id: string | number) => string;
  retakePath: string;
};

type PdfHandle = {
  handleDownload: () => Promise<void>;
};

type PdfPayload = {
  id: number;
  kind: MajorResultKind;
  requestId: number;
  result: any;
};

const MAJOR_RESULT_CONFIGS: MajorResultConfig[] = [
  {
    key: 'meaningTestLastResult',
    kind: 'meaning',
    title: 'Sens® - test des 10 valeurs',
    path: id => `/resultat-meaning-value/${id}`,
    retakePath: getMajorTestConfig('meaning').retakePath,
  },
  {
    key: 'needsTestLastResult',
    kind: 'needs',
    title: 'Needs® - test des 7 besoins professionnels et personnels',
    path: id => `/resultat-need/${id}`,
    retakePath: getMajorTestConfig('needs').retakePath,
  },
  {
    key: 'needsProTestLastResult',
    kind: 'needsPro',
    title: 'Needs® pro - test des 7 besoins professionnels',
    path: id => `/resultat-need-pro/${id}`,
    retakePath: getMajorTestConfig('needsPro').retakePath,
  },
  {
    key: 'preferenceTestLastResult',
    kind: 'preference',
    title: 'Préférences® - test des 32 personnalités',
    path: id => `/resultat-preference/${id}`,
    retakePath: getMajorTestConfig('preference').retakePath,
  },
  {
    key: 'harmonyTestLastResult',
    kind: 'harmony',
    title: 'Harmony® - test des 16 soft skills',
    path: id => `/resultat-harmony/${id}`,
    retakePath: getMajorTestConfig('harmony').retakePath,
  },
  {
    key: 'studyTestLastResult',
    kind: 'study',
    title: "Study® - test d'orientation",
    path: id => `/resultat-study/${id}`,
    retakePath: getMajorTestConfig('study').retakePath,
  },
  {
    key: 'preferenceJeuneLastResult',
    kind: 'preferenceJeune',
    title: 'Préférences® Jeunes',
    path: id => `/res-pref-j/${id}`,
    retakePath: getMajorTestConfig('preferenceJeune').retakePath,
  },
];

const getDateParts = (createdAt: string) => {
  const [date, time] = dateFormat(new Date(createdAt), 'dd/mm/yyyy, HH:MM').split(', ');

  return {
    date,
    time,
  };
};

const formatMobileDate = (createdAt: string) => {
  const date = new Date(createdAt);

  return {
    date: date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
    time: dateFormat(date, 'HH:MM'),
  };
};

const normalize = (value: string) => value.toLowerCase();

const buildMajorResult = (test: any, config: MajorResultConfig): ResultItem => {
  const { date, time } = getDateParts(test.createdAt);

  return {
    id: test.id,
    title: config.title,
    date,
    time,
    createdAt: test.createdAt,
    searchText: normalize(`${date} ${time} ${config.title}`),
    consultPath: config.path(test.id),
    retakePath: config.retakePath,
    category: 'major',
    majorKind: config.kind,
  };
};

const buildMinorResult = (test: any): ResultItem => {
  const { date, time } = getDateParts(test.createdAt);
  const title = test?.test?.title || '';

  return {
    id: test.id,
    title,
    date,
    time,
    createdAt: test.createdAt,
    searchText: normalize(`${date} ${time} ${title}`),
    consultPath: `/mes-resultats/${test.id}`,
    retakePath: test?.test?.id ? `/test/${test.test.id}/${encodeURIComponent(title)}` : undefined,
    category: 'minor',
  };
};

const buildGameResult = (game: CompletedGameResult): ResultItem => {
  const { date, time } = getDateParts(game.createdAt);

  return {
    id: game.idResult,
    title: game.title,
    date,
    time,
    createdAt: game.createdAt,
    searchText: normalize(`${date} ${time} ${game.title}`),
    consultPath: `/game/result/${game.idResult}`,
    category: 'game',
    gameId: game.idGame,
  };
};
const MY_RESULTS_RESTORE_KEY = 'my-results-restore-item';

const getResultDomId = (item: Pick<ResultItem, 'category' | 'id'>) =>
  `my-result-${item.category}-${item.id}`;

const scrollResultIntoView = (element: HTMLElement) => {
  const container = document.querySelector<HTMLElement>('[data-redesign-scroll-container]');

  if (!container) {
    element.scrollIntoView({ behavior: 'auto', block: 'center' });
    return;
  }

  const previousBehavior = container.style.scrollBehavior;
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const top = Math.max(
    0,
    elementRect.top -
      containerRect.top +
      container.scrollTop -
      container.clientHeight / 2 +
      elementRect.height / 2
  );

  container.style.scrollBehavior = 'auto';
  container.scrollTo({ top, behavior: 'auto' });
  container.style.scrollBehavior = previousBehavior;
};

const hydrateHarmonyResult = (result: any) => {
  const scoreTotal =
    result.problem_solving +
    result.confidence +
    result.collective_sense +
    result.communication +
    result.creativity +
    result.critical_mind +
    result.curiousity +
    result.daring +
    result.emotional_intelligence +
    result.empathy +
    result.entrepreneurial_spirit +
    result.motivation +
    result.presence +
    result.stress_management +
    result.time_management +
    result.vision;

  return {
    ...result,
    opt_in: result.opt_in ? 'Oui' : 'Non',
    bc: getBC(result.bc),
    scoreTotal,
    realization_period: getPeriodeRealisation(result.realization_period as any),
  };
};

const MyResults = () => {
  const classes = useMyResultsStyles();
  const sharedClasses = useSharedStyles();
  const history = useHistory();
  const alert = useAlert();
  const isMobile = useMediaQuery(`(max-width: ${redesignTheme.breakpoints.mobile}px)`);

  const { getUserAllTestLatestResult } = useMajorTest();
  const { setDialog, setMyResult } = useMyResulatOfTest();
  const { getMyResult: getMeaningResult } = useMeaningValueTestResult();
  const { getMyResult: getNeedsResult } = useNeedsTestResult();
  const { getMyResult: getNeedsProResult } = useNeedsProTestResult();
  const { getMyResult: getPreferenceResult, getMyResultJeune } =
    usePreferencePersonalityTestResult();
  const { getMyResult: getStudyResult } = useStudyTestResult();

  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState<ResultItem[]>([]);
  const [downloadingResultId, setDownloadingResultId] = useState<string | number | null>(null);
  const [restartingResultId, setRestartingResultId] = useState<string | number | null>(null);
  const [pdfPayload, setPdfPayload] = useState<PdfPayload | null>(null);
  const pdfRef = useRef<PdfHandle>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const restoreItemKeyRef = useRef<string | null | undefined>(undefined);

  if (restoreItemKeyRef.current === undefined) {
    restoreItemKeyRef.current = sessionStorage.getItem(MY_RESULTS_RESTORE_KEY);
  }

  const restoreScrollAnchor = restoreItemKeyRef.current
    ? `my-result-${restoreItemKeyRef.current}`
    : undefined;

  useEffect(() => {
    if (loading || !restoreItemKeyRef.current) {
      return undefined;
    }

    const restoreKey = restoreItemKeyRef.current;
    let cancelled = false;

    const scrollToRestoredResult = () => {
      if (cancelled) {
        return;
      }

      const element = document.getElementById(`my-result-${restoreKey}`);

      if (element) {
        scrollResultIntoView(element);
      }
    };

    const scrollTimeoutId = window.setTimeout(scrollToRestoredResult, 50);
    const clearTimeoutId = window.setTimeout(() => {
      sessionStorage.removeItem(MY_RESULTS_RESTORE_KEY);
    }, 1000);

    return () => {
      cancelled = true;
      window.clearTimeout(scrollTimeoutId);
      window.clearTimeout(clearTimeoutId);
    };
  }, [loading, results]);

  useEffect(() => {
    let isMounted = true;

    const getResults = async () => {
      setLoading(true);
      setDialog({});
      setMyResult({});

      try {
        const [testsOutcome, gamesOutcome] = await Promise.allSettled([
          getUserAllTestLatestResult(),
          fetchCompletedGameResults(),
        ]);
        const allTests = testsOutcome.status === 'fulfilled' ? testsOutcome.value : {};
        const completedGames = gamesOutcome.status === 'fulfilled' ? gamesOutcome.value : [];

        if (testsOutcome.status === 'rejected' && gamesOutcome.status === 'rejected') {
          throw testsOutcome.reason;
        }

        const majorResults = MAJOR_RESULT_CONFIGS.reduce<ResultItem[]>((acc, config) => {
          const test = allTests?.[config.key];

          if (test) {
            acc.push(buildMajorResult(test, config));
          }

          return acc;
        }, []);
        const minorResults = (allTests?.customTestResults || [])
          .map(buildMinorResult)
          .filter((item: ResultItem) => item.title.trim());
        const gameResults = completedGames.map(buildGameResult);

        if (isMounted) {
          setResults(majorResults.concat(minorResults, gameResults));
        }
      } catch (error) {
        alert.openSimpleAlert();
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    getResults();

    return () => {
      isMounted = false;
    };
    // The project hooks return fresh function references, so this mirrors the legacy one-time fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizedSearch = normalize(searchValue.trim());
  const filteredResults = useMemo(() => {
    if (!normalizedSearch) {
      return results;
    }

    return results.filter(result => result.searchText.includes(normalizedSearch));
  }, [normalizedSearch, results]);

  const majorResults = filteredResults.filter(result => result.category === 'major');
  const minorResults = filteredResults.filter(result => result.category === 'minor');
  const gameResults = filteredResults.filter(result => result.category === 'game');
  const totalMajorResults = results.filter(result => result.category === 'major').length;
  const totalMinorResults = results.filter(result => result.category === 'minor').length;
  const totalTestResults = totalMajorResults + totalMinorResults;

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const getHarmonyResult = async (id: number) => {
    const token = getFromLS('userToken');
    const userConnected = JSON.parse(getFromLS('userInfos') || '{}');

    if (!token) {
      history.push('/');
      return null;
    }

    if (userConnected?.company_user_role?.id === 5) {
      const response = await getMe(token);
      const userResults = response.data?.results || [];

      return userResults.map(hydrateHarmonyResult).find((result: any) => result.id === id) || null;
    }

    const resultsData = await ResultServices.getResultByTest(token, id, 1);
    const historyResults = resultsData?.data?.history || [];

    return historyResults.map(hydrateHarmonyResult).find((result: any) => result.id === id) || null;
  };

  const getMajorResultForPdf = async (kind: MajorResultKind, id: number) => {
    const fetchers: Record<MajorResultKind, () => Promise<any>> = {
      meaning: async () => {
        const { data } = await getMeaningResult(id);
        return data.data;
      },
      needs: async () => {
        const { data } = await getNeedsResult(id);
        return data.data;
      },
      needsPro: async () => {
        const { data } = await getNeedsProResult(id);
        return data.data;
      },
      preference: async () => {
        const { data } = await getPreferenceResult(id);
        return data.data;
      },
      harmony: async () => getHarmonyResult(id),
      study: async () => {
        const { data } = await getStudyResult(id);
        return data.data;
      },
      preferenceJeune: async () => {
        const { data } = await getMyResultJeune(id);
        return data.data;
      },
    };

    return fetchers[kind]();
  };

  const handleDownloadPdf = async (item: ResultItem) => {
    if (!item.majorKind) {
      return;
    }

    setDownloadingResultId(item.id);

    try {
      const result = await getMajorResultForPdf(item.majorKind, +item.id);

      if (!result) {
        alert.openSimpleAlert();
        setDownloadingResultId(null);
        return;
      }

      setPdfPayload({
        id: +item.id,
        kind: item.majorKind,
        requestId: Date.now(),
        result,
      });
    } catch (error) {
      alert.openSimpleAlert();
      setDownloadingResultId(null);
    }
  };

  useEffect(() => {
    if (!pdfPayload) {
      return undefined;
    }

    let cancelled = false;

    // PDF components fetch user data and briefly mount then unmount while loading.
    // Require the export markup to stay present across two polls before downloading.
    const downloadWhenReady = async () => {
      const deadline = Date.now() + 15000;
      let stableChecks = 0;

      try {
        while (!cancelled && Date.now() < deadline) {
          const hasContent = Boolean(pdfContainerRef.current?.firstElementChild);

          if (hasContent && pdfRef.current?.handleDownload) {
            stableChecks += 1;

            if (stableChecks >= 2) {
              if (!cancelled) {
                // eslint-disable-next-line no-await-in-loop -- download only after stable polls
                await pdfRef.current.handleDownload();
              }

              return;
            }
          } else {
            stableChecks = 0;
          }

          // eslint-disable-next-line no-await-in-loop -- delay between readiness polls
          await new Promise<void>(resolve => {
            window.setTimeout(resolve, 50);
          });
        }

        if (!cancelled) {
          alert.openSimpleAlert();
        }
      } finally {
        if (!cancelled) {
          setDownloadingResultId(null);
        }
      }
    };

    downloadWhenReady();

    return () => {
      cancelled = true;
    };
    // alert.openSimpleAlert is a stable hook callback for this page flow
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfPayload]);

  const renderDownloadButton = (
    item: ResultItem,
    className?: string,
    label = 'Télécharger le PDF'
  ) => {
    if (!item.majorKind) {
      return null;
    }

    const isDownloading = downloadingResultId === item.id;

    return (
      <button
        type="button"
        className={className ? `${classes.secondaryButton} ${className}` : classes.secondaryButton}
        disabled={isDownloading}
        aria-label={`Télécharger le PDF ${item.title}`}
        onClick={() => handleDownloadPdf(item)}>
        {isDownloading ? (
          <CircularProgress className={classes.smallLoader} size={16} />
        ) : (
          <GetAppIcon className={classes.buttonIcon} />
        )}
        {label}
      </button>
    );
  };

  const handleRetake = async (item: ResultItem) => {
    if (item.category !== 'game') {
      openMyResultRetake(
        { category: item.category, retakePath: item.retakePath },
        history.push
      );
      return;
    }

    if (!item.gameId || restartingResultId) {
      return;
    }

    setRestartingResultId(item.id);

    try {
      const response = await apiServiceNative.put(`/api/restart-game-result/${item.id}`, {});
      const newIdResult = getRestartedResultId(response);

      if (!newIdResult) {
        alert.openSimpleAlert();
        return;
      }

      history.push(`/game/view/${item.gameId}/${newIdResult}`);
    } catch (error) {
      alert.openSimpleAlert();
    } finally {
      setRestartingResultId(null);
    }
  };

  const renderRetakeButton = (
    item: ResultItem,
    className?: string,
    label = item.category === 'game' ? 'Recommencer' : 'Refaire le test'
  ) => {
    if (item.category !== 'game' && !item.retakePath) {
      return null;
    }

    const isRestarting = restartingResultId === item.id;

    return (
      <button
        type="button"
        className={className ? `${classes.secondaryButton} ${className}` : classes.secondaryButton}
        aria-label={`Refaire ${item.title}`}
        disabled={isRestarting}
        onClick={() => handleRetake(item)}>
        {isRestarting ? (
          <CircularProgress className={classes.smallLoader} size={16} />
        ) : (
          <ReplayIcon className={classes.buttonIcon} />
        )}
        {label}
      </button>
    );
  };

  const handleConsult = (item: ResultItem) => {
    sessionStorage.setItem(MY_RESULTS_RESTORE_KEY, `${item.category}-${item.id}`);
    history.push(item.consultPath);
  };

  const renderConsultButton = (item: ResultItem, className?: string) => (
    <button
      type="button"
      className={className ? `${classes.primaryButton} ${className}` : classes.primaryButton}
      aria-label={`Consulter ${item.title}`}
      onClick={() => handleConsult(item)}>
      Consulter
      <ArrowForwardIcon className={classes.buttonIcon} />
    </button>
  );

  const renderActions = (item: ResultItem) => (
    <div className={classes.actions}>
      {renderDownloadButton(item)}
      {renderRetakeButton(item)}
      {renderConsultButton(item)}
    </div>
  );

  const renderMobileActions = (item: ResultItem) => {
    const isMajor = item.category === 'major';
    const retakeLabel = item.category === 'game' ? 'Recommencer' : 'Refaire le test';

    return (
      <div
        className={
          isMajor ? classes.cardActions : `${classes.cardActions} ${classes.cardActionsMinor}`
        }>
        {isMajor ? (
          <div className={classes.cardActionsRow}>
            {renderDownloadButton(item, classes.mobileDownloadButton, 'Télécharger PDF')}
            {renderRetakeButton(item, classes.mobileRetakeButton, retakeLabel)}
          </div>
        ) : (
          renderRetakeButton(item, classes.mobileFullWidthButton, retakeLabel)
        )}
        {renderConsultButton(item, classes.mobilePrimaryButton)}
      </div>
    );
  };

  const renderMobileList = (items: ResultItem[]) => (
    <div className={classes.mobileList}>
      {items.map(item => {
        const { date, time } = formatMobileDate(item.createdAt);

        return (
          <article
            key={`${item.category}-${item.id}`}
            id={getResultDomId(item)}
            className={classes.resultCard}>
            <div className={classes.cardBody}>
              <h3 className={classes.cardTitle}>{item.title}</h3>
              <div className={classes.cardMeta}>
                <span>{date}</span>
                <span>{time}</span>
              </div>
            </div>
            {renderMobileActions(item)}
          </article>
        );
      })}
    </div>
  );

  const renderTable = (items: ResultItem[]) => (
    <div className={classes.tableScroll}>
      <table className={classes.table}>
        <thead className={classes.tableHead}>
          <tr>
            <th className={classes.tableHeaderCell} scope="col">
              Date
            </th>
            <th className={classes.tableHeaderCell} scope="col">
              Test
            </th>
            <th
              className={`${classes.tableHeaderCell} ${classes.tableHeaderActionCell}`}
              scope="col">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr
              key={`${item.category}-${item.id}`}
              id={getResultDomId(item)}
              className={classes.resultRow}>
              <td className={classes.tableDateCell}>
                <div className={classes.dateValue}>{item.date}</div>
                <div className={classes.timeValue}>{item.time}</div>
              </td>
              <td className={classes.tableTestCell}>{item.title}</td>
              <td className={classes.tableActionCell}>{renderActions(item)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderPdfDocument = () => {
    if (!pdfPayload) {
      return null;
    }

    const pdfKey = `${pdfPayload.kind}-${pdfPayload.id}-${pdfPayload.requestId}`;

    switch (pdfPayload.kind) {
      case 'meaning':
        return (
          <MeaningResultPDF
            key={pdfKey}
            ref={pdfRef}
            myResult={pdfPayload.result}
            idResult={pdfPayload.id}
          />
        );
      case 'needs':
        return (
          <NeedsResultPDF
            key={pdfKey}
            ref={pdfRef}
            myResult={pdfPayload.result}
            idResult={pdfPayload.id}
          />
        );
      case 'needsPro':
        return (
          <NeedsProResultPDF
            key={pdfKey}
            ref={pdfRef}
            myResult={pdfPayload.result}
            idResult={pdfPayload.id}
          />
        );
      case 'preference':
        return <PreferenceResultPDF key={pdfKey} ref={pdfRef} myResult={pdfPayload.result} />;
      case 'harmony':
        return (
          <HarmonyResultPDF
            key={pdfKey}
            ref={pdfRef}
            myResult={pdfPayload.result}
            idResult={pdfPayload.id}
          />
        );
      case 'study':
        return (
          <StudyResultPDF
            key={pdfKey}
            ref={pdfRef}
            myResult={pdfPayload.result}
            idResult={pdfPayload.id}
          />
        );
      case 'preferenceJeune':
        return (
          <PreferenceJeuneResultPDF
            key={pdfKey}
            ref={pdfRef}
            myResult={pdfPayload.result}
            idResult={pdfPayload.id}
          />
        );
      default:
        return null;
    }
  };

  const renderSection = (title: string, tag: string, description: string, items: ResultItem[]) => {
    if (items.length === 0) {
      return null;
    }

    return (
      <section className={classes.section}>
        <SectionHeader
          tag={tag}
          title={title}
          description={description}
          descriptionVariant="brown"
        />
        {isMobile ? renderMobileList(items) : renderTable(items)}
      </section>
    );
  };

  const renderEmptyState = () => (
    <EmptyState
      tall={true}
      narrow={true}
      title="Aucun résultat trouvé"
      description={
        searchValue.trim() ? (
          <>
            Aucun test ne correspond à votre recherche{' '}
            <strong>&quot;{searchValue.trim()}&quot;</strong>. Essayez avec d&apos;autres mots-clés.
          </>
        ) : (
          'Aucun test passé ne correspond à votre profil.'
        )
      }
    />
  );

  if (loading) {
    return (
      <RedesignLayout footer={true} noPadding={true}>
        <div className={clsx(sharedClasses.pageShell, classes.page)}>
          <div className={sharedClasses.loadingCenter}>
            <CircularProgress />
          </div>
        </div>
      </RedesignLayout>
    );
  }

  return (
    <RedesignLayout footer={true} noPadding={true} scrollAnchor={restoreScrollAnchor}>
      <main className={clsx(sharedClasses.pageShell, classes.page)}>
        <PageHeader
          breadcrumbs={[{ label: 'Accueil', href: '/main' }, { label: 'Mes résultats' }]}
          title="Mes résultats"
          description="L'historique complet de vos tests passés. Consultez vos résultats à tout moment, téléchargez-les en PDF, ou repassez un test pour mettre à jour votre profil."
          titleVariant="primary"
          contentClassName={classes.headerContent}
          content={
            <SearchInput
              value={searchValue}
              onChange={handleSearchChange}
              ariaLabel="Rechercher dans mes résultats"
              className={classes.headerSearch}
            />
          }
          actions={
            <div className={classes.stats} aria-label="Statistiques des résultats">
              <div className={classes.statItem}>
                <span className={classes.statValue}>{totalTestResults}</span>
                <span className={classes.statLabel}>Tests passés</span>
              </div>
              <div className={classes.statDivider} aria-hidden="true" />
              <div className={classes.statItem}>
                <span className={classes.statValue}>{totalMajorResults}</span>
                <span className={classes.statLabel}>Tests majeurs</span>
              </div>
              <div className={classes.statDivider} aria-hidden="true" />
              <div className={classes.statItem}>
                <span className={classes.statValue}>{totalMinorResults}</span>
                <span className={classes.statLabel}>Tests thématiques</span>
              </div>
            </div>
          }
        />

        {filteredResults.length === 0 ? (
          renderEmptyState()
        ) : (
          <div className={classes.sections}>
            {renderSection(
              'Vos tests majeurs',
              'Tests standardisés',
              'Les fondations de votre profil. Conçus par nos psychologues, validés scientifiquement, ils dessinent votre cartographie complète.',
              majorResults
            )}
            {renderSection(
              'Vos tests thématiques',
              'Tests non standardisés',
              'Tous vos tests courts et ciblés, classés par date de passage.',
              minorResults
            )}
            {renderSection(
              'Vos serious games',
              'Serious games',
              'Vos mises en situation ludiques et vos scores, classés par date de passage.',
              gameResults
            )}
          </div>
        )}
        <div ref={pdfContainerRef} className={classes.pdfContainer}>
          {renderPdfDocument()}
        </div>
      </main>
    </RedesignLayout>
  );
};

export default MyResults;
